import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { api, unwrap } from '@/api/client';
import { qk } from '@/api/queries';
import type { User } from '@/api/types';
import { Button, Chip, FieldHint, Input, Modal, ModalFooter, ModalHeader, Segmented, useModalTitleId, useToast } from '@/design-system';
import { errorText } from '@/shell/errorText';
import s from './settings.module.css';

/** The scopes an Admin may grant to a PM (apps/api/app/core/scopes.py GRANTABLE). */
export const GRANTABLE = ['deployment:publish_production', 'subscription:override_price', 'organization:bulk_create'] as const;
const key = (scope: string) => scope.replace(':', '__') as 'deployment__publish_production';

/** «Добавить PM»: POST /admin/users (the API creates PMs only; an Admin comes from a server script). */
export function AddUserModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const titleId = useModalTitleId();
  const toast = useToast();
  const qc = useQueryClient();
  const [f, setF] = useState({ full_name: '', email: '', password: '' });
  const [locale, setLocale] = useState<'ru' | 'uz'>('ru');
  const [problem, setProblem] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: () => unwrap(api.POST('/admin/users', { body: { full_name: f.full_name.trim(), email: f.email.trim(), password: f.password, locale } })),
    onSuccess: (u) => {
      void qc.invalidateQueries({ queryKey: qk.users });
      toast(t('users_edit.created', { name: u.full_name }));
      onClose();
    },
    onError: (err) => setProblem(errorText(err, tc)),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setProblem(null);
    create.mutate();
  };
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }));
  return (
    <Modal open onClose={onClose} labelledBy={titleId}>
      <ModalHeader id={titleId} title={t('users_edit.add_title')} sub={t('users_edit.add_sub')} onClose={onClose} closeLabel={tc('actions.close')} />
      <form className={s.form} onSubmit={submit}>
        <Input label={t('users_edit.full_name')} value={f.full_name} onChange={set('full_name')} required autoComplete="off" />
        <Input label={t('users_edit.email')} type="email" value={f.email} onChange={set('email')} required autoComplete="off" />
        <Input label={t('users_edit.password')} type="password" value={f.password} onChange={set('password')} minLength={10} required autoComplete="new-password" />
        <div className={s.group}>
          <span className={s.groupLabel}>{t('users_edit.locale')}</span>
          <Segmented variant="form" label={t('users_edit.locale')} value={locale} onChange={setLocale} className={s.alignStart} options={[{ value: 'ru', label: 'RU' }, { value: 'uz', label: 'UZ' }]} />
        </div>
        {problem && (
          <span className={s.problem} role="alert">
            {problem}
          </span>
        )}
        <ModalFooter padTop>
          <Button size={46} variant="secondary" onClick={onClose}>
            {tc('actions.cancel')}
          </Button>
          <Button size={46} type="submit" disabled={create.isPending}>
            {t('users_edit.create')}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}

/** A PM's access: the grantable extra scopes and the active flag. PATCH /admin/users/{id} (audited). */
export function UserAccessModal({ user, onClose }: { user: User; onClose: () => void }) {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const titleId = useModalTitleId();
  const toast = useToast();
  const qc = useQueryClient();
  const admin = user.role === 'admin';
  const [scopes, setScopes] = useState<string[]>(user.extra_scopes);
  const [active, setActive] = useState(user.is_active);
  const [problem, setProblem] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => {
      const body: { extra_scopes?: string[]; is_active?: boolean } = {};
      if ([...scopes].sort().join() !== [...user.extra_scopes].sort().join()) body.extra_scopes = scopes;
      if (active !== user.is_active) body.is_active = active;
      return unwrap(api.PATCH('/admin/users/{user_id}', { params: { path: { user_id: user.id } }, body }));
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.users });
      toast(t('users_edit.saved', { name: user.full_name }));
      onClose();
    },
    onError: (err) => setProblem(errorText(err, tc)),
  });
  const toggle = (scope: string) => setScopes((xs) => (xs.includes(scope) ? xs.filter((x) => x !== scope) : [...xs, scope]));
  return (
    <Modal open onClose={onClose} labelledBy={titleId}>
      <ModalHeader id={titleId} title={t('users_edit.access_title', { name: user.full_name })} sub={admin ? t('users_edit.admin_note') : t('users_edit.access_sub')} onClose={onClose} closeLabel={tc('actions.close')} />
      {!admin && (
        <div className={s.form}>
          <div className={s.group}>
            <span className={s.groupLabel}>{t('users_edit.scopes')}</span>
            {GRANTABLE.map((scope) => (
              <div key={scope} className={s.scopeRow}>
                <Chip variant="accent" icon={scopes.includes(scope) ? 'check' : undefined} selected={scopes.includes(scope)} onClick={() => toggle(scope)}>
                  {t(`users_edit.scope.${key(scope)}`)}
                </Chip>
                <FieldHint>{t(`users_edit.scope_hint.${key(scope)}`)}</FieldHint>
              </div>
            ))}
          </div>
          <div className={s.group}>
            <span className={s.groupLabel}>{t('users_edit.active')}</span>
            <Segmented
              variant="form"
              label={t('users_edit.active')}
              value={active ? 'on' : 'off'}
              onChange={(v) => setActive(v === 'on')}
              className={s.alignStart}
              options={[
                { value: 'on', label: t('users_edit.active_on') },
                { value: 'off', label: t('users_edit.active_off') },
              ]}
            />
          </div>
          {problem && (
            <span className={s.problem} role="alert">
              {problem}
            </span>
          )}
        </div>
      )}
      <ModalFooter>
        <Button size={46} variant="secondary" onClick={onClose}>
          {tc('actions.cancel')}
        </Button>
        {!admin && (
          <Button size={46} onClick={() => save.mutate()} disabled={save.isPending}>
            {t('users_edit.save')}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
}
